import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Save, SaveAll } from 'lucide-react';
import { ArrowTabStepper, Button, Card, useToast } from '@kss/ui';
import {
  validateEmail,
  validateMobile,
  validateGst,
  validatePan,
  validateAadhaar,
  type FieldValidator,
} from '../utils/formatValidators';

const inputCls =
  'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const textareaCls =
  'min-h-[88px] w-full rounded-md border border-border bg-surface-card px-3 py-2 text-sm text-content-primary outline-none focus:border-primary';

export interface FieldDef {
  key: string;
  label: string;
  type?:
    | 'text'
    | 'email'
    | 'tel'
    | 'mobile'
    | 'date'
    | 'number'
    | 'integer'
    | 'decimal'
    | 'pan'
    | 'gst'
    | 'aadhaar'
    | 'textarea'
    | 'select';
  options?: { value: string; label: string }[];
  required?: boolean;
  placeholder?: string;
  /** Cap for `type: number / integer / decimal` inputs. */
  min?: number;
  max?: number;
  /** Cap for `type: text / email / tel / mobile / pan / gst / aadhaar`. */
  maxLength?: number;
  /** Custom validator: return null on valid, or a short error string. */
  validate?: FieldValidator;
}

export interface ModuleFormConfig {
  /** Page title (already translated). */
  title: string;
  /** Where to return on cancel/save. */
  listPath: string;
  /** Multi-step layout: each tab declares the fields it owns. */
  tabs: { key: string; label: string; fields: FieldDef[] }[];
  /** Optional save handler. If omitted, the form just shows a toast and navigates back. */
  onSave?: (state: Record<string, string>) => Promise<void> | void;
  /** Optional review summary renderer. */
  reviewExtras?: (state: Record<string, string>) => ReactNode;
}

/**
 * Generic ArrowTabStepper-driven form. Mirrors the company-form pattern
 * — every "Add new" page across the panel feels identical.
 *
 * Per-tab buttons:
 *   • Back            — previous tab (hidden on tab 1)
 *   • Save & Next     — validate, fire onSave (treated as a draft), advance
 *   • Next            — validate, advance without saving
 *   • Save            — final tab only
 *
 * Per-field guarantees:
 *   • Numeric types accept digits only; the keystroke is rejected before
 *     it lands in state. Decimal type also accepts a single dot.
 *   • Email / mobile / PAN / GST / Aadhaar are validated on tab change
 *     and on save; PAN/GST auto-uppercase; mobile strips non-digits.
 *   • All fields can override with a custom `validate` predicate.
 */
export function ModulePlaceholderForm({
  title,
  listPath,
  tabs,
  onSave,
  reviewExtras,
}: ModuleFormConfig) {
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation('panel');
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Per-tab state for the arrow-tab strip — see NewCompany.tsx for the
  // full state-machine description.
  const [savedIndices, setSavedIndices] = useState<number[]>([]);
  const [visitedIndices, setVisitedIndices] = useState<number[]>([]);
  const markVisited = (i: number) =>
    setVisitedIndices((p) => (p.includes(i) ? p : [...p, i]));
  const markSaved = (i: number) => {
    setSavedIndices((p) => (p.includes(i) ? p : [...p, i]));
    setVisitedIndices((p) => p.filter((x) => x !== i));
  };

  const stepDefs = useMemo(() => tabs.map((tab) => ({ key: tab.key, label: tab.label })), [tabs]);
  const isLast = step === tabs.length - 1;

  const set = (k: string, v: string) => {
    setState((s) => ({ ...s, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: '' }));
  };

  function fieldError(f: FieldDef, raw: string): string | null {
    const v = (raw ?? '').trim();
    if (f.required && !v) return `${f.label} is required`;
    if (!v) return null; // optional + empty — fine

    if (f.maxLength && v.length > f.maxLength) {
      return `${f.label} must be at most ${f.maxLength} characters`;
    }

    switch (f.type) {
      case 'email':
        return validateEmail(v);
      case 'mobile':
      case 'tel':
        return validateMobile(v);
      case 'pan':
        return validatePan(v);
      case 'gst':
        return validateGst(v);
      case 'aadhaar':
        return validateAadhaar(v);
      case 'number':
      case 'integer':
      case 'decimal': {
        const n = Number(v);
        if (!Number.isFinite(n)) return `${f.label} must be a number`;
        if (f.type === 'integer' && !Number.isInteger(n)) return `${f.label} must be an integer`;
        if (typeof f.min === 'number' && n < f.min) return `${f.label} must be ≥ ${f.min}`;
        if (typeof f.max === 'number' && n > f.max) return `${f.label} must be ≤ ${f.max}`;
        return null;
      }
      default:
        break;
    }
    if (f.validate) return f.validate(v);
    return null;
  }

  function validateTab(index: number): boolean {
    const tab = tabs[index];
    if (!tab) return true;
    const next: Record<string, string> = {};
    let firstErr: string | null = null;
    for (const f of tab.fields) {
      const err = fieldError(f, state[f.key] ?? '');
      if (err) {
        next[f.key] = err;
        if (!firstErr) firstErr = err;
      }
    }
    setErrors((prev) => ({ ...prev, ...next }));
    if (firstErr) toast.error('Fix errors', firstErr);
    return !firstErr;
  }

  function validateAll(): boolean {
    const next: Record<string, string> = {};
    let firstErr: string | null = null;
    for (const tab of tabs) {
      for (const f of tab.fields) {
        const err = fieldError(f, state[f.key] ?? '');
        if (err) {
          next[f.key] = err;
          if (!firstErr) firstErr = err;
        }
      }
    }
    setErrors(next);
    if (firstErr) toast.error('Fix errors', firstErr);
    return !firstErr;
  }

  // Numeric-only key filter: blocks anything that isn't a digit (or a
  // single decimal point for decimal fields). Allows control keys for
  // copy/paste/navigation.
  function onNumericKeyDown(allowDecimal: boolean) {
    return (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const k = e.key;
      const isControl = ['Backspace','Tab','Enter','Escape','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End','Delete'].includes(k);
      if (isControl) return;
      const isDigit = k >= '0' && k <= '9';
      const isDot = k === '.';
      const allowed = isDigit || (allowDecimal && isDot && !e.currentTarget.value.includes('.'));
      if (!allowed) e.preventDefault();
    };
  }

  function onNumericPaste(allowDecimal: boolean) {
    return (e: React.ClipboardEvent<HTMLInputElement>) => {
      const txt = e.clipboardData.getData('text');
      const ok = allowDecimal ? /^-?\d*\.?\d*$/.test(txt) : /^-?\d+$/.test(txt);
      if (!ok) e.preventDefault();
    };
  }

  async function handleSave({ andContinue = false }: { andContinue?: boolean } = {}) {
    if (andContinue) {
      if (!validateTab(step)) return;
    } else {
      if (!validateAll()) return;
    }

    setSaving(true);
    try {
      if (onSave) await onSave(state);
      // Mark the current tab as committed (green check).
      markSaved(step);
      if (andContinue && !isLast) {
        toast.success('Draft saved', `Moved to "${tabs[step + 1].label}"`);
        setStep((s) => Math.min(s + 1, tabs.length - 1));
      } else {
        toast.success('Saved', `${title.replace(/^new\s+/i, '')} created`);
        navigate(listPath);
      }
    } catch (e) {
      toast.error('Save failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  }

  function handleNext() {
    if (!validateTab(step)) return;
    // Forward Next — the tab passed validation, flag it GREEN.
    // RED is reserved for tabs the user revisits and abandons via Back.
    markSaved(step);
    setStep((s) => Math.min(s + 1, tabs.length - 1));
  }

  function renderField(f: FieldDef) {
    const val = state[f.key] ?? '';
    const err = errors[f.key];
    const labelEl = (
      <span className="text-xs font-medium text-content-secondary">
        {f.label}
        {f.required && <span className="text-danger"> *</span>}
      </span>
    );

    if (f.type === 'textarea') {
      return (
        <label key={f.key} className="flex flex-col gap-1 md:col-span-2">
          {labelEl}
          <textarea
            className={textareaCls}
            value={val}
            maxLength={f.maxLength}
            placeholder={f.placeholder}
            onChange={(e) => set(f.key, e.target.value)}
          />
          {err && <span className="text-xs text-danger">{err}</span>}
        </label>
      );
    }

    if (f.type === 'select') {
      return (
        <label key={f.key} className="flex flex-col gap-1">
          {labelEl}
          <select className={inputCls} value={val} onChange={(e) => set(f.key, e.target.value)}>
            <option value="">—</option>
            {f.options?.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {err && <span className="text-xs text-danger">{err}</span>}
        </label>
      );
    }

    const isNumeric = f.type === 'number' || f.type === 'integer' || f.type === 'decimal';
    const allowDecimal = f.type === 'number' || f.type === 'decimal';

    // PAN/GST auto-upper, mobile/aadhaar strip non-digits, otherwise pass-through.
    const transform = (raw: string): string => {
      if (f.type === 'pan' || f.type === 'gst') return raw.toUpperCase();
      if (f.type === 'aadhaar') return raw.replace(/\D/g, '');
      return raw;
    };

    const inputType =
      f.type === 'email' ? 'email'
      : f.type === 'tel' || f.type === 'mobile' ? 'tel'
      : f.type === 'date' ? 'date'
      : isNumeric ? 'text' /* keep `text` so we can custom-filter */
      : 'text';

    return (
      <label key={f.key} className="flex flex-col gap-1">
        {labelEl}
        <input
          type={inputType}
          className={inputCls}
          value={val}
          placeholder={f.placeholder}
          maxLength={f.maxLength}
          inputMode={isNumeric ? (allowDecimal ? 'decimal' : 'numeric') : undefined}
          autoComplete="off"
          onKeyDown={isNumeric ? onNumericKeyDown(allowDecimal) : undefined}
          onPaste={isNumeric ? onNumericPaste(allowDecimal) : undefined}
          onChange={(e) => set(f.key, transform(e.target.value))}
          onBlur={() => {
            const e = fieldError(f, state[f.key] ?? '');
            setErrors((p) => ({ ...p, [f.key]: e ?? '' }));
          }}
        />
        {err && <span className="text-xs text-danger">{err}</span>}
      </label>
    );
  }

  const reviewTabIndex = tabs.findIndex((tt) => tt.key === 'review');

  return (
    <div className="-m-6 p-[3px]">
      <Card>
        {/* Half-height header — same compact pattern used on NewCompany. */}
        <div className="mb-1.5 flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => navigate(listPath)}
            className="inline-flex h-6 w-6 items-center justify-center rounded border border-border text-content-secondary hover:bg-surface-card-hover"
            title="Back"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
          </button>
          <h2 className="text-sm font-semibold text-content-primary">{title}</h2>
        </div>

        <div className="mb-2.5">
          <ArrowTabStepper
            compact
            steps={stepDefs}
            activeIndex={step}
            savedIndices={savedIndices}
            visitedIndices={visitedIndices}
            onChange={(i) => {
              if (i > step) {
                if (!validateTab(step)) return;
                markSaved(step);   // forward jump → previous = green
              } else if (i < step) {
                markVisited(step); // backward jump → abandoned = red
              }
              setStep(i);
            }}
          />
        </div>

        {step === reviewTabIndex && reviewTabIndex !== -1 ? (
          <div className="grid gap-3 text-sm">
            <div className="grid gap-2 rounded-md border border-border p-4 md:grid-cols-2">
              {tabs
                .flatMap((tab) => tab.fields)
                .map((f) => (
                  <div key={f.key}>
                    <span className="text-content-muted">{f.label}: </span>
                    <span className="font-medium">{state[f.key] || '—'}</span>
                  </div>
                ))}
            </div>
            {reviewExtras?.(state)}
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">{tabs[step]?.fields.map(renderField)}</div>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          <div className="text-xs text-content-muted">
            {t('stepper.step', { current: step + 1, total: tabs.length })}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => navigate(listPath)} disabled={saving}>
              Cancel
            </Button>
            {step > 0 && (
              <Button
                variant="outline"
                onClick={() => {
                  // Backward Back — flag the abandoned tab RED.
                  markVisited(step);
                  setStep((s) => s - 1);
                }}
                disabled={saving}
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
            )}
            {!isLast && (
              <>
                <Button
                  variant="outline"
                  onClick={() => handleSave({ andContinue: true })}
                  disabled={saving}
                  title="Save the current step as a draft and move to the next"
                >
                  <SaveAll className="h-4 w-4" />
                  {saving ? 'Saving…' : 'Save & Next'}
                </Button>
                <Button onClick={handleNext} disabled={saving}>
                  Next <ArrowRight className="h-4 w-4" />
                </Button>
              </>
            )}
            {isLast && (
              <Button onClick={() => handleSave()} disabled={saving}>
                <Save className="h-4 w-4" />
                {saving ? 'Finishing…' : 'Finish'}
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

export default ModulePlaceholderForm;
